// MATERIAL UI
import { Box, Container, Divider, Skeleton, Stack, Typography } from "@mui/material";

// COMPONENTS
import { SecondarySwitch } from "src/components/common/SecondarySwitch";
import Title from "src/components/common/Title";
import Layout from "src/components/core/Layout";
import BackLink from "src/components/tax-center/BackLink";
import DispositionMethodCard from "src/components/tax-center/DispositionMethodCard";
import TaxAccountGate from "src/components/tax-center/TaxAccountGate";

// REDUX
import api from "src/store/features/api";

// STYLES & UTILS
import {
  BORDER,
  BORDER_LIGHT,
  Card,
  NAVY,
  TEXT_SECONDARY,
} from "src/components/tax-center/styles";
import { formatUtcDate } from "src/components/tax-center/utils";

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between">
    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: NAVY }}>{value}</Typography>
  </Stack>
);

const TaxCenterSettings = () => {
  const taxYear = new Date().getUTCFullYear();
  const accountQuery = api.useGetTaxAccountQuery();

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3, display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack spacing={2.75} sx={{ maxWidth: 760 }}>
          <BackLink to="/tax-center" />
          <Box>
            <Title
              title={{ partA: "Tax", partB: "settings" }}
              subtitle="These preferences apply to the current, unreported tax year."
            />
          </Box>
        </Stack>

        <TaxAccountGate>
          <Stack direction={{ xs: "column", lg: "row" }} gap={3} alignItems="flex-start">
            <Stack spacing={2.75} sx={{ flex: 1, maxWidth: 760 }}>
              <DispositionMethodCard />

              <Card sx={{ p: 3.25, display: "flex", flexDirection: "column", gap: 2.25 }}>
                <Stack spacing={0.75}>
                  <Typography sx={{ fontSize: 17, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
                    Form delivery
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
                    How you receive your 1099-DA and other tax forms.
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2.5}
                  sx={{ px: 2.25, py: 2, border: `1px solid ${BORDER}`, borderRadius: "6px" }}>
                  <Stack spacing={0.5}>
                    <Typography sx={{ fontSize: 15, fontWeight: 500, color: NAVY, lineHeight: 1.3 }}>
                      Paperless delivery
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.5 }}>
                      Forms are delivered electronically in the Tax Center only. Paper delivery
                      isn't currently available.
                    </Typography>
                  </Stack>
                  <SecondarySwitch checked disabled />
                </Stack>
              </Card>
            </Stack>

            <Stack spacing={2} sx={{ width: { xs: "100%", lg: 360 }, flex: "none" }}>
              <Card sx={{ p: 2.75, display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: NAVY, lineHeight: 1.3 }}>
                  Changing your method
                </Typography>
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.55 }}>
                  All unreported gains for the current tax year are recalculated when you change
                  method. Tax years already reported are unaffected.
                </Typography>
                <Divider sx={{ borderColor: BORDER_LIGHT }} />
                <Row
                  label="Current method"
                  value={
                    accountQuery.isLoading ? (
                      <Skeleton width={44} />
                    ) : (
                      (accountQuery.data?.disposition_method ?? "—")
                    )
                  }
                />
                <Row label="Applies from" value={`Tax year ${taxYear}`} />
                <Row
                  label="Last changed"
                  value={
                    accountQuery.isLoading ? (
                      <Skeleton width={72} />
                    ) : (
                      formatUtcDate(accountQuery.data?.date_modified)
                    )
                  }
                />
              </Card>

              <Box
                sx={{
                  backgroundColor: NAVY,
                  borderRadius: "8px",
                  p: 2.75,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>
                  Not sure which to pick?
                </Typography>
                <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", lineHeight: 1.55 }}>
                  Our support article walks you through how each method affects your reported gains.
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </TaxAccountGate>
      </Container>
    </Layout>
  );
};

export default TaxCenterSettings;
